/**
 * Shared vehicle-detail sections used by BOTH the full vehicle page
 * (pages/VehicleTuning.jsx) and the Tuning Database cascade result
 * (pages/TuningDatabase.jsx). Single source of markup so the two views can
 * never drift apart — same payload shape (`_gain_out` + tool_compat), same
 * rendering. testPrefix scopes data-testids per surface ("vt" / "tcv" / "td").
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, FileUp } from "lucide-react";
import { PORTAL_URL } from "@/lib/site";

export const STAGE_LABELS = { eco: "ECO", stage1: "Stage 1", stage2: "Stage 2", stage3: "Stage 3", e85: "E85", e85plus: "E85+" };

/** Year range exactly as the full vehicle page derives it: prefer the
 * generation's own range, else year_from/to, never inventing "present". */
export function vehicleYears(v) {
  if (!v) return "";
  const m = (v.generation || "").match(/((?:19|20)\d{2})\s*[–—-]\s*((?:19|20)\d{2}|present)/i);
  if (m) return m[2].toLowerCase() === "present" ? `${m[1]}–present` : `${m[1]}–${m[2]}`;
  if (v.year_from && v.year_to) return `${v.year_from}–${v.year_to}`;
  if (v.year_from) return `${v.year_from}–`;
  return "";
}

function Gain({ value, original, unit, testId }) {
  if (value == null || original == null) return null;
  const g = value - original;
  const pct = original ? Math.round((g / original) * 100) : null;
  if (g <= 0) return null;
  return (
    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-700" data-testid={testId}>
      +{g} {unit}{pct != null ? ` · +${pct}%` : ""}
    </p>
  );
}

/** Specification chips incl. ECU designation (first listed control unit). */
export function VehicleSpecChips({ v, years, testPrefix }) {
  const ecu = v?.ecus?.find((e) => (e.designation || "").trim());
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid={`${testPrefix}-specs`}>
      {[v.generation, years, v.engine_size, v.fuel, ecu ? `ECU: ${[ecu.maker, ecu.designation].filter(Boolean).join(" ")}` : null]
        .filter(Boolean)
        .map((chip) => (
          <span key={chip} className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid={`${testPrefix}-chip-${String(chip).slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}>
            {chip}
          </span>
        ))}
    </div>
  );
}

/** Detailed tuning data: stock vs every published stage, with per-stage and
 * total Stage 1 gain figures. */
export function VehicleStageTable({ v, testPrefix }) {
  const stages = (v?.display_stages || []).filter((s) => STAGE_LABELS[s.key]);
  return (
    <section className="border border-line" data-testid={`${testPrefix}-table-section`}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] bg-white text-left" data-testid={`${testPrefix}-table`}>
          <thead>
            <tr className="border-b border-line">
              <th className="px-4 py-3 sm:px-6"><span className="sr-only">Measure</span></th>
              <th className="px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 sm:px-6">Stock</th>
              {stages.map((s) => (
                <th key={s.key} className="bg-regal-amber px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-white sm:px-6" data-testid={`${testPrefix}-col-${s.key}`}>
                  {STAGE_LABELS[s.key]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {["bhp", "nm"].map((unit) => (
              <tr key={unit} className="border-b border-line last:border-0" data-testid={`${testPrefix}-row-${unit}`}>
                <td className="px-4 py-4 sm:px-6">
                  <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">{unit === "bhp" ? "Power" : "Torque"}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">{unit === "bhp" ? "BHP" : "Nm"}</p>
                </td>
                <td className="px-4 py-4 text-center sm:px-6" data-testid={`${testPrefix}-stock-${unit}`}>
                  <p className="font-display text-2xl sm:text-3xl font-extrabold text-neutral-400">{(unit === "bhp" ? v.original_bhp : v.original_nm) ?? "—"}</p>
                </td>
                {stages.map((s) => (
                  <td key={s.key} className="bg-regal-amber/5 px-4 py-4 text-center sm:px-6" data-testid={`${testPrefix}-${s.key}-${unit}`}>
                    <p className="font-display text-2xl sm:text-3xl font-extrabold text-regal-amber">{s[unit] ?? "—"}</p>
                    <Gain value={s[unit]} original={unit === "bhp" ? v.original_bhp : v.original_nm} unit={unit === "bhp" ? "BHP" : "Nm"} testId={`${testPrefix}-gain-${s.key}-${unit}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {v.gain_bhp != null && (
        <div className="grid grid-cols-2 gap-px border-t border-line bg-line" data-testid={`${testPrefix}-gain-summary`}>
          <div className="bg-surface p-5 text-center">
            <p className="font-display text-3xl font-extrabold text-emerald-700" data-testid={`${testPrefix}-gain-bhp`}>+{v.gain_bhp} BHP</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">Stage 1 power gain</p>
          </div>
          <div className="bg-surface p-5 text-center">
            <p className="font-display text-3xl font-extrabold text-emerald-700" data-testid={`${testPrefix}-gain-nm`}>{v.gain_nm != null ? `+${v.gain_nm} Nm` : "—"}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">Stage 1 torque gain</p>
          </div>
        </div>
      )}
    </section>
  );
}

/** Confirmed service capabilities for one vehicle, three-state policy:
 *  available   -> shown as available (emerald)
 *  unavailable -> source explicitly states unavailable (muted, never green)
 *  not_stated  -> OMITTED entirely (never claimed either way)
 *  Scraped navigation/menu text never reaches this list (cleaned at import). */
export const CAPABILITY_LABELS = {
  stage1: "Stage 1", stage2: "Stage 2", stage3: "Stage 3",
  dpf_off: "DPF off", egr_off: "EGR off", adblue_off: "AdBlue/SCR off",
  gpf_off: "GPF/OPF off", o2_lambda_off: "O2/lambda off", dtc_off: "DTC off",
  decat: "Decat", pops_bangs: "Pops & bangs", hardcut_ruptor: "Hard-cut / popcorn limiter",
  vmax_off: "Vmax off", speed_limiter: "Speed limiter", rev_limiter_removal: "Rev-limiter removal",
  cold_start_off: "Cold-start off", swirl_flap_off: "Swirl-flap off", exhaust_flap: "Exhaust-flap control",
  stop_start_off: "Start/stop off", immobiliser: "Immobiliser", launch_control: "Launch control",
  e85_flexfuel: "E85 / flex-fuel", eco_tuning: "Eco tuning", gearbox_tuning: "Gearbox/TCU tuning",
  drive_mode_enhancement: "Drive-mode enhancement", diagnostics: "Diagnostics", dyno: "Dyno",
  back_to_standard: "Back to standard", checksum: "Checksum", restore: "Restore",
  emotion_module: "Emotion module", sport_display: "Sport display",
  cylinder_on_demand_off: "Cylinder-on-demand off", dsg_farts: "DSG farts",
  hot_start_fix: "Hot-start fix", nox_off: "NOx off", maf_delete: "MAF delete",
  evap_off: "EVAP off", sap_off: "SAP off", coasting_mode_off: "Coasting-mode off",
  low_idle: "Low idle", readiness_calibration: "Readiness calibration",
  fuel_pump_calibration: "Fuel-pump calibration", injector_calibration: "Injector calibration",
  coolant_temperature_management: "Coolant-temperature management",
  oil_pressure_fix: "Oil-pressure fix", map_sensor_calibration: "MAP-sensor calibration",
  octane_adaptation: "Octane adaptation",
};

export function ServiceCapabilities({ caps, testPrefix }) {
  if (!caps) return null;
  const keys = Object.keys(CAPABILITY_LABELS).filter((k) => k in caps);
  const available = keys.filter((k) => caps[k] === "available");
  const unavailable = keys.filter((k) => caps[k] === "unavailable");
  if (!available.length && !unavailable.length) return null;
  return (
    <section className="mt-10 border border-line" data-testid={`${testPrefix}-services`}>
      <div className="border-b border-line bg-surface px-6 py-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber">Available services for this vehicle</p>
        <p className="mt-2 text-sm text-neutral-500">
          Confirmed from the underlying tuning-data sources. Anything not listed here is not stated in the source data — ask us and we will confirm.
        </p>
      </div>
      {available.length > 0 && (
        <div className="flex flex-wrap gap-2 bg-white p-6" data-testid={`${testPrefix}-services-available`}>
          {available.map((k) => (
            <span key={k} className="border border-emerald-700/30 bg-emerald-50 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-emerald-700" data-testid={`${testPrefix}-service-${k}`}>
              {CAPABILITY_LABELS[k]}
            </span>
          ))}
        </div>
      )}
      {unavailable.length > 0 && (
        <div className="border-t border-line bg-surface px-6 py-4" data-testid={`${testPrefix}-services-unavailable`}>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">Listed as unavailable in the source data</p>
          <div className="flex flex-wrap gap-2">
            {unavailable.map((k) => (
              <span key={k} className="border border-line bg-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-400 line-through decoration-neutral-300" data-testid={`${testPrefix}-service-unavailable-${k}`}>
                {CAPABILITY_LABELS[k]}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}


/** Upload File / Request a Quote actions — identical on the full vehicle page
 * and underneath the Tuning Database result. */
export function VehicleDetailCtas({ v, quoteUrl, testPrefix }) {
  return (
    <div className="mt-8 flex flex-col gap-3 border border-line bg-white p-6 sm:flex-row sm:items-center sm:p-8" data-testid={`${testPrefix}-ctas`}>
      <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors" data-testid={`${testPrefix}-cta-portal`}>
        <FileUp size={15} /> Upload a File
      </a>
      <Link to={quoteUrl} className="inline-flex items-center justify-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid={`${testPrefix}-cta-quote`}>
        Request a Quote <ArrowUpRight size={15} />
      </Link>
      <p className="text-xs text-neutral-500 sm:ml-auto sm:max-w-[240px]">
        Existing customers upload their ECU file via the <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="text-regal-amber underline-offset-4 hover:underline">Regal Tuning File Portal</a>. New to Regal? Request a quote first.
      </p>
    </div>
  );
}
